<template lang="pug">
  .programs-container
    slot(:pageNumber='pageNumber')
    .row.middle.center.m-top-2
      k-select.m-right-auto.size-select(
        v-if='controlSize'
        withBorder
        :options='sizesOptions'
        name='pageSize'
        keyName='value'
        labelName='value'
        :labelIn='$t("paginatedTable.show")'
        small
        @selectOption='changePageSize'
        :preselectedOption='currentPageSize'
      )
      .pagination-container.row.center.middle
        button.arrow-button.row.middle.center(
          type='button'
          :disabled='disablePrev'
          @click='goToPage(pageNumber - 1)'
          :class='{ hide: totalCount === 0 }'
        )
          simple-svg.dark-gray-icon(:filepath='leftArrowIcon' width='16px' height='16px')
        button.box.text-normal(type='button' :class='{ hide: pageNumber < 3 || lastPage < 5 }' @click='goToPage(firstPage)')
          | 1
        button.text-normal.prev-arrow(type='button' :class='{ hide: pageNumber < 4 || lastPage < 5 }' @click='prevFivePages()')
          simple-svg.icon.dots-icon(:filepath='dotsIcon' :width='"20px"' :height='"20px"')
          simple-svg.icon.blue-hover-icon.arrow-icon.rot-left(:filepath='arrowIcon' :width='"20px"' :height='"20px"')
        button.box.text-normal(
          v-for='page in visiblePages'
          :key='page'
          :class='{ selected: pageNumber === page }'
          @click='goToPage(page)'
          type='button'
        )
          | {{ page + 1}}
        button.text-normal.next-arrow(type='button' :class='{ hide: lastPage - pageNumber < 4 || lastPage < 5}' @click='nextFivePages()')
          simple-svg.icon.dots-icon(:filepath='dotsIcon' :width='"20px"' :height='"20px"')
          simple-svg.icon.blue-hover-icon.arrow-icon.rot-right(:filepath='arrowIcon' :width='"20px"' :height='"20px"')
        button.box.text-normal(type='button' :class='{ hide: lastPage - pageNumber < 3 || lastPage < 5 }' @click='goToPage(lastPage)')
          | {{ lastPage + 1 }}
        button.arrow-button.row.middle.center(
          type='button'
          :disabled='disableNext'
          @click='goToPage(pageNumber + 1)'
          :class='{ hide: totalCount === 0 }'
        )
          simple-svg.dark-gray-icon(:filepath='rightArrowIcon' width='16px' height='16px')
</template>

<script>
import dotsIcon from '@/assets/ic_page_dots.svg'
import arrowIcon from '@/assets/ic_double-chevron.svg'
import leftArrowIcon from '@/assets/ic_chevron-left.svg'
import rightArrowIcon from '@/assets/ic_chevron-right.svg'

import KSelect from '@/components/KSelect'
import { defaultPageSize, firstPage } from '@/utils/constants'
import { paginatedTableSizes } from '../utils/constants'

export default {
  components: { KSelect },
  props: {
    getPage: { type: Function, required: true },
    pageSize: { type: Number, default: defaultPageSize },
    totalCount: { type: Number, default: 0 },
    controlSize: { type: Boolean }
  },
  data() {
    return {
      pageNumber: 0,
      currentPageSize: defaultPageSize,
      firstPage,
      dotsIcon,
      arrowIcon,
      rightArrowIcon,
      leftArrowIcon,
      backIcon: dotsIcon,
      nextIcon: dotsIcon
    }
  },
  watch: {
    pageSize() {
      this.currentPageSize = this.pageSize
    }
  },
  created() {
    this.currentPageSize = this.pageSize
  },
  computed: {
    lastPage() {
      return Math.ceil(this.totalCount / this.currentPageSize) - 1
    },
    disablePrev() {
      return this.pageNumber <= 0
    },
    disableNext() {
      return this.pageNumber >= this.lastPage
    },
    visiblePages() {
      let firstPageOfArray = 0
      let i = 0
      const amountOfElements = Math.min(this.lastPage + 1, 5)
      const pagesArray = Array(amountOfElements)
      if (amountOfElements === 5) {
        if (this.pageNumber === 0) firstPageOfArray = this.pageNumber
        else if (this.pageNumber === 1) firstPageOfArray = this.pageNumber - 1
        else if (this.pageNumber === this.lastPage - 1) firstPageOfArray = this.pageNumber - 3
        else if (this.pageNumber === this.lastPage) firstPageOfArray = this.pageNumber - 4
        else firstPageOfArray = this.pageNumber - 2
      } else {
        firstPageOfArray = 0
      }
      while (i < amountOfElements) pagesArray[i++] = firstPageOfArray++
      return pagesArray
    },
    sizesOptions() {
      return paginatedTableSizes
    }
  },
  methods: {
    async goToPage(newPage) {
      this.pageNumber = newPage
      await this.getPage(newPage, this.currentPageSize)
      this.$emit('pageChanged', this.pageNumber)
    },
    prevFivePages() {
      if (this.pageNumber <= 6) this.goToPage(firstPage, this.currentPageSize)
      else this.goToPage(this.pageNumber - 5, this.currentPageSize)
      this.$emit('pageChanged', this.pageNumber)
    },
    nextFivePages() {
      if (this.pageNumber >= this.lastPage - 5) this.goToPage(this.lastPage, this.currentPageSize)
      else this.goToPage(this.pageNumber + 5, this.currentPageSize)
      this.$emit('pageChanged', this.pageNumber)
    },
    async changePageSize(name, value) {
      this.pageNumber = 0
      this.currentPageSize = value
      await this.getPage(this.pageNumber, this.currentPageSize)
      this.$emit('pageChanged', this.pageNumber)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.size-select{
  max-width: 120px;
}

.programs-container {
  max-width: $content-size;
  min-height: calc(100vh - #{$footer-height}- 30px);
  width: 100%;
}

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pagination-container {
  & > :not(:last-child) {
    margin-right: 16px;
  }
}

.arrow-button{
  border: 1px solid $gray;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  &:hover {
    background-color: $light-gray;
    color: $white;
  }
}

.box {
  border: 1px solid $gray;
  border-radius: 4px;
  height: 32px;
  text-align: center;
  width: 32px;

  &:hover,
  &.selected {
    background-color: $blue;
    border: 1px solid $blue;
    color: $white;
  }

  &.selected {
    font-weight: bold;
  }
}

.icon {
  height: 20px;
  width: 20px;
}

.text-button {
  &:disabled {
    color: $dark-gray;
    cursor: not-allowed;

    &:hover {
      color: $dark-gray;
    }
  }
}

.hide {
  opacity: 0;
}

.prev-arrow,
.next-arrow {
  .arrow-icon {
    display: none;
  }

  .rot-left {
    transform: rotate(-90deg);
  }

  .rot-right {
    transform: rotate(90deg);
  }

  &:hover {
    .dots-icon {
      display: none;
    }

    .arrow-icon {
      display: block;
    }
  }
}
</style>
